import { memo } from 'react';
import type { Move } from '../../types';

interface ChecklistItemProps {
  move: Move;
  onToggle: (id: number) => void;  // Toggle done/active
  onTap: (move: Move) => void;     // Open detail sheet
}

const URGENCY_BADGE: Record<string, { bg: string; text: string; label: string }> = {
  overdue: { bg: 'bg-red-500/20', text: 'text-red-500', label: 'Overdue' },
  time_sensitive: { bg: 'bg-amber-500/20', text: 'text-amber-500', label: 'Soon' },
};

const MOVE_TYPE_ICONS: Record<string, string> = {
  follow_up: 'chat',
  send_document: 'description',
  schedule: 'event',
  call: 'call',
  other: 'task_alt',
};

export const ChecklistItem = memo(function ChecklistItem({ move, onToggle, onTap }: ChecklistItemProps) {
  const isDone = move.status === 'done';
  const isSkipped = move.status === 'skipped';
  const isCompleted = isDone || isSkipped;
  const urgencyBadge = move.urgency && URGENCY_BADGE[move.urgency];
  const typeIcon = move.move_type ? (MOVE_TYPE_ICONS[move.move_type] ?? 'task_alt') : 'task_alt';
  const isOutgoing = move.direction === 'i_promised';

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggle(move.id);
  };

  const handleRowClick = () => {
    onTap(move);
  };

  return (
    <div
      onClick={handleRowClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleRowClick();
        }
      }}
      className={`flex items-center gap-3 px-4 py-3 bg-white rounded-2xl border border-edge/50 cursor-pointer active:scale-[0.98] transition-all hover:border-edge ${
        isCompleted ? 'opacity-50' : ''
      }`}
    >
      {/* Checkbox */}
      <button
        onClick={handleCheckboxClick}
        aria-label={isCompleted ? 'Mark as active' : 'Mark as done'}
        className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-all ${
          isDone
            ? 'bg-green-500 shadow-sm shadow-green-200'
            : isSkipped
            ? 'bg-ink-tertiary/30'
            : 'border-2 border-edge hover:border-ink-tertiary'
        }`}
      >
        {isDone && (
          <span className="material-icons-round text-white" style={{ fontSize: '14px' }}>check</span>
        )}
        {isSkipped && (
          <span className="material-icons-round text-ink-tertiary" style={{ fontSize: '14px' }}>remove</span>
        )}
      </button>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p
          className={`text-sm font-medium leading-tight ${
            isCompleted ? 'line-through text-ink-tertiary' : 'text-ink'
          }`}
        >
          {move.label}
        </p>
        {move.contact_name && (
          <p className="text-xs text-ink-secondary mt-0.5 truncate">{move.contact_name}</p>
        )}
        {urgencyBadge && !isCompleted && (
          <span
            className={`inline-block mt-1 text-[10px] font-bold px-1.5 py-0.5 rounded ${urgencyBadge.bg} ${urgencyBadge.text}`}
          >
            {urgencyBadge.label}
          </span>
        )}
      </div>

      {/* Type icon */}
      <span className={`material-icons-round flex-shrink-0 ${isCompleted ? 'text-ink-tertiary' : 'text-ink-secondary'}`} style={{ fontSize: '18px' }}>
        {typeIcon}
      </span>

      {/* Direction arrow */}
      <span
        className={`material-icons-round flex-shrink-0 ${isCompleted ? 'text-ink-tertiary' : isOutgoing ? 'text-primary' : 'text-ink-secondary'}`}
        style={{ fontSize: '16px' }}
        title={isOutgoing ? 'You promised' : 'They promised'}
      >
        {isOutgoing ? 'arrow_upward' : 'arrow_downward'}
      </span>
    </div>
  );
});

export default ChecklistItem;
